import {
  DehydratedRetailWarcraftTalentTreeNode,
  RetailWarcraftTalentTreeBlueprint,
  RetailWarcraftTalentTreeBuildNode,
} from '@resources/js/typings/viewModels/retailWarcraftTalentTreeBuildNode';
import { TalentTreeFullProps } from '@resources/js/components/TalentTreeFull/TalentTreeFull';

const talentTreeBuildNodeForDehydratedRetailWarcraftTalentTreeNode = (
  talentTreeBlueprint: RetailWarcraftTalentTreeBlueprint,
  [abilityId, pointsInvested]: DehydratedRetailWarcraftTalentTreeNode
): RetailWarcraftTalentTreeBuildNode | undefined => {
  for (const node of talentTreeBlueprint.changeSet.allNodes) {
    const entry = node.abilities.find((ability) => ability.id === abilityId);
    if (entry && 'treeType' in node) {
      return {
        node,
        entry,
        pointsInvested: pointsInvested ?? 0,
      };
    }
  }
};

const diffSelectedNodes = (
  a: DehydratedRetailWarcraftTalentTreeNode[],
  b: DehydratedRetailWarcraftTalentTreeNode[]
) => {
  return b.filter(
    ([baseAbilityId]) =>
      !a.some(
        ([alternativeAbilityId]) => baseAbilityId === alternativeAbilityId
      )
  );
};

export const talentTreeDifferences = (
  talentTreeBlueprint: RetailWarcraftTalentTreeBlueprint | undefined,
  baseTalentTree: TalentTreeFullProps,
  alternativeTalentTree: TalentTreeFullProps
): {
  addedTalents: RetailWarcraftTalentTreeBuildNode[];
  removedTalents: RetailWarcraftTalentTreeBuildNode[];
} => {
  const addedTalents: RetailWarcraftTalentTreeBuildNode[] = [];
  const removedTalents: RetailWarcraftTalentTreeBuildNode[] = [];
  if (talentTreeBlueprint === undefined) {
    return {
      addedTalents,
      removedTalents,
    };
  }

  const nodesRemoved = diffSelectedNodes(
    alternativeTalentTree.dehydratedBuild.selectedNodes,
    baseTalentTree.dehydratedBuild.selectedNodes
  );
  nodesRemoved.forEach((node) => {
    const buildNode =
      talentTreeBuildNodeForDehydratedRetailWarcraftTalentTreeNode(
        talentTreeBlueprint,
        node
      );
    if (buildNode) removedTalents.push(buildNode);
  });

  for (const alternativeNode of alternativeTalentTree.dehydratedBuild
    .selectedNodes) {
    const baseNode = baseTalentTree.dehydratedBuild.selectedNodes.find(
      ([abilityId]) => abilityId === alternativeNode[0]
    );
    if (baseNode === undefined) {
      const buildNode =
        talentTreeBuildNodeForDehydratedRetailWarcraftTalentTreeNode(
          talentTreeBlueprint,
          alternativeNode
        );
      if (buildNode) addedTalents.push(buildNode);
    } else if ((baseNode[1] ?? 0) !== (alternativeNode[1] ?? 0)) {
      const delta = (alternativeNode[1] ?? 0) - (baseNode[1] ?? 0);
      const buildNode =
        talentTreeBuildNodeForDehydratedRetailWarcraftTalentTreeNode(
          talentTreeBlueprint,
          [alternativeNode[0], Math.abs(delta)]
        );
      if (buildNode) {
        if (delta < 0) {
          removedTalents.push(buildNode);
        } else {
          addedTalents.push(buildNode);
        }
      }
    }
  }

  return {
    addedTalents,
    removedTalents,
  };
};
